import styled from '@emotion/styled'
import { ApiContentSection, ApiSectionType } from '@storefronts/common-types'
import React, { useContext } from 'react'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_CTA_FILL_COLOR_VAR,
    SECONDARY_CTA_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { PageContext } from '../../../context/PageContextProvider'
import { useLandingPageLinksForHashes } from '../../../hooks/useLandingPageLinksForHashes'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import { shouldDeprioritizeCardImage } from '../../../utils/getFirstLcpImageSectionIndex'
import { ProductCard } from '../../shared/ProductCard'
import { SectionContainer } from './Shared'
import { scaledFontSize, ScrollingLayout } from '../../../style/scrollingLayout'

type UpsellBoxSectionBox = Extract<
    ApiContentSection,
    { type: ApiSectionType.UPSELL_BOX }
>

const UpsellBoxContainer = styled.div({
    maxWidth: ScrollingLayout.PROSE_MAX_WIDTH,
    marginInline: 'auto',
    border: `1px solid var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    // Match standard 8px radius used across cards and media containers
    borderRadius: '0.2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 10,
})

const UpsellTitle = styled.h2({
    ...getTypographyRoleStyle('featureTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 500,
    }),
    marginTop: 0,
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
    marginBottom: 0,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
})

const UpsellSubtitle = styled.h3({
    ...getTypographyRoleStyle('bodyText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    marginBottom: 0,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    marginTop: 8,
    textAlign: 'center',
})

export const UpsellBox: React.FC<{
    section: UpsellBoxSectionBox
    isBelowFold?: boolean
}> = ({ section, isBelowFold = true }) => {
    const { setActiveLPLink } = useContext(PageContext)
    const resolvedLinks = useLandingPageLinksForHashes(section.linkHashes)

    return (
        <SectionContainer>
            <UpsellBoxContainer>
                {section.title && <UpsellTitle>{section.title}</UpsellTitle>}
                {section.subtitle && (
                    <UpsellSubtitle>{section.subtitle}</UpsellSubtitle>
                )}
                {resolvedLinks.map((link, i) => (
                    <div
                        key={link.linkHash}
                        style={{
                            width: '100%',
                            marginTop: i === 0 ? 16 : 0,
                        }}
                    >
                        <ProductCard
                            background={`var(${PRIMARY_PAGE_FILL_COLOR_VAR})`}
                            buttonColor={`var(${SECONDARY_CTA_FILL_COLOR_VAR})`}
                            buttonTextColor={`var(${SECONDARY_CTA_TEXT_COLOR_VAR})`}
                            color={`var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`}
                            lpLink={link}
                            showRatingCount={false}
                            onClick={(_e, ctx) => setActiveLPLink(link, ctx)}
                            deprioritizeImage={shouldDeprioritizeCardImage({
                                isBelowFold,
                                cardIndex: i,
                                firstVisibleCount: 1,
                            })}
                        />
                    </div>
                ))}
            </UpsellBoxContainer>
        </SectionContainer>
    )
}
